// Read the (untrusted) roles out of a session token payload, for UX routing/guarding only.
//
// The /admin route must not blindly reuse whatever session token is in sessionStorage: after
// /demo/traffic or /demo/field, that token is a TrafficOfficer/FieldOfficer token with no
// admin rights, and calling /api/v1/admin/* with it just yields 403s. Decoding the payload
// lets the client AVOID those calls and show an actionable message (or route to a demo admin
// entry) instead. This is NOT a security check — the server remains authoritative on every
// admin endpoint; we only use it to decide which token (if any) is worth sending.
//
// Token format (app-service SessionTokenService): base64url(payloadJson).base64url(sig),
// payload = { userId, displayName, roles[], exp }. We read only `roles` and never log tokens.

const ADMIN_ROLES = ["Administrator", "ITViewer"] as const;

export function decodeTokenRoles(token: string | null | undefined): string[] {
  if (!token) return [];
  const dot = token.indexOf(".");
  const payloadB64 = dot > 0 ? token.slice(0, dot) : "";
  if (!payloadB64) return [];
  try {
    const obj = JSON.parse(base64UrlDecode(payloadB64)) as { roles?: unknown };
    if (!Array.isArray(obj.roles)) return [];
    return obj.roles.filter((r): r is string => typeof r === "string");
  } catch {
    return [];
  }
}

/** True when the token's payload roles include an admin-console role (Administrator/ITViewer). */
export function isAdminToken(token: string | null | undefined): boolean {
  const roles = decodeTokenRoles(token);
  return roles.some((r) => (ADMIN_ROLES as readonly string[]).includes(r));
}

function base64UrlDecode(b64url: string): string {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(b64url.length / 4) * 4, "=");
  const binary = atob(b64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
